import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileCheck2,
  FileText,
  Lock,
  LucideIcon,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  TimerReset,
} from 'lucide-react';
import { SurgeonLayout } from '@/components/surgeon/SurgeonLayout';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { ClinicAuthWorkspace } from '@/components/surgeon/v2/ClinicAuthWorkspace';
import { TodaysORWorkspace } from '@/components/surgeon/v2/TodaysORWorkspace';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { scheduleSurgeonWorkspaceScrollToTop } from '@/lib/surgeon-console/workspaceScroll';
import { cn } from '@/lib/utils';

type Workspace = 'command-center' | 'clinic-auth' | 'todays-or';

interface ScheduledCaseMetricRow {
  id: string;
  status: string | null;
  scheduled_date: string | null;
}

interface AuthorizationMetricRow {
  id: string;
  status: string | null;
  scheduled_date: string | null;
  updated_at: string | null;
  denial_category: string | null;
  payer_normalized_status: string | null;
  cpt_codes: string[] | null;
  modifiers: string[] | null;
}

interface ConsoleMetrics {
  clinicReady: number;
  payerPending: number;
  rcmExceptions: number;
  orReady: number;
  signedToday: number;
  revenueIntegrityScore: number;
  dataUnavailable: boolean;
}

interface MetricCardConfig {
  label: string;
  value: string;
  detail: string;
  icon: LucideIcon;
  tone: 'primary' | 'success' | 'warning' | 'danger';
}

const WORKSPACES: Array<{
  id: Workspace;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    id: 'command-center',
    label: 'Command Center',
    description: 'Revenue and denial control',
    icon: Activity,
  },
  {
    id: 'clinic-auth',
    label: 'Pre-Auth',
    description: 'Build the authorized universe',
    icon: ClipboardList,
  },
  {
    id: 'todays-or',
    label: 'Post-Op',
    description: 'Reconcile and sign the note',
    icon: CalendarClock,
  },
];

const CLOSED_LOOP_STAGES = [
  {
    title: 'Intake',
    owner: 'MA / Staff',
    gate: 'Patient, payer, procedure, docs',
    risk: 'Wrong patient, missing clinical proof',
    icon: FileText,
  },
  {
    title: 'Authorize',
    owner: 'Surgeon',
    gate: 'CPT bundle + medical necessity',
    risk: 'Under-auth, payer threshold miss',
    icon: ClipboardCheck,
  },
  {
    title: 'Operate',
    owner: 'Surgeon',
    gate: 'Dictation captures actual work',
    risk: 'Performed work not documented',
    icon: Stethoscope,
  },
  {
    title: 'Reconcile',
    owner: 'LINK',
    gate: 'Authorized = documented = confirmed',
    risk: 'Revenue leakage or denial exposure',
    icon: ShieldCheck,
  },
  {
    title: 'Export',
    owner: 'RCM',
    gate: 'Clean claim package',
    risk: 'Modifier conflict, missing attestation',
    icon: FileCheck2,
  },
];

const GUARDRAILS = [
  'No code leaves the console unless it exists in the authorized bundle.',
  'AI narrative generation receives only evidence-supported CPT lines.',
  'Surgeon confirmation is required before a post-op CPT becomes billable.',
  'RCM exceptions stay visible until routed, corrected, or explicitly attested.',
];

const EMPTY_CONSOLE_METRICS: ConsoleMetrics = {
  clinicReady: 0,
  payerPending: 0,
  rcmExceptions: 0,
  orReady: 0,
  signedToday: 0,
  revenueIntegrityScore: 91,
  dataUnavailable: false,
};

const DEMO_CONSOLE_METRICS: ConsoleMetrics = {
  clinicReady: 8,
  payerPending: 5,
  rcmExceptions: 2,
  orReady: 4,
  signedToday: 6,
  revenueIntegrityScore: 94,
  dataUnavailable: false,
};

async function fetchConsoleMetrics(): Promise<ConsoleMetrics> {
  try {
    const todayPrefix = new Date().toISOString().slice(0, 10);

    const [scheduledResult, authResult] = await Promise.all([
      supabase
        .from('scheduled_cases')
        .select('id,status,scheduled_date')
        .eq('status', 'scheduled'),
      supabase
        .from('authorization_requests')
        .select('id,status,scheduled_date,updated_at,denial_category,payer_normalized_status,cpt_codes,modifiers'),
    ]);

    if (scheduledResult.error || authResult.error) {
      throw scheduledResult.error || authResult.error;
    }

    const scheduledRows = (scheduledResult.data || []) as unknown as ScheduledCaseMetricRow[];
    const authRows = (authResult.data || []) as unknown as AuthorizationMetricRow[];

    const rcmExceptions = authRows.filter((row) =>
      row.status === 'more_info_requested' ||
      row.status === 'payer_denied' ||
      Boolean(row.denial_category) ||
      row.payer_normalized_status === 'NEED_MORE_INFO' ||
      row.payer_normalized_status === 'DENIED',
    ).length;

    const orReady = authRows.filter((row) =>
      row.status === 'payer_approved' ||
      row.status === 'awaiting_post_op_reconciliation',
    ).length;

    const payerPending = authRows.filter((row) =>
      row.status === 'submitted_to_payer' ||
      row.status === 'payer_pending',
    ).length;

    const signedToday = authRows.filter((row) =>
      row.status === 'surgeon_approved' &&
      typeof row.updated_at === 'string' &&
      row.updated_at.startsWith(todayPrefix),
    ).length;

    const denominator = authRows.length + scheduledRows.length;
    const riskBurden = denominator > 0 ? rcmExceptions / denominator : 0;
    const revenueIntegrityScore = Math.max(72, Math.round(98 - riskBurden * 60));

    return {
      clinicReady: scheduledRows.length,
      payerPending,
      rcmExceptions,
      orReady,
      signedToday,
      revenueIntegrityScore,
      dataUnavailable: false,
    };
  } catch {
    return {
      ...EMPTY_CONSOLE_METRICS,
      dataUnavailable: true,
    };
  }
}

function getMetricCards(metrics: ConsoleMetrics): MetricCardConfig[] {
  return [
    {
      label: 'Ready for Pre-Auth',
      value: String(metrics.clinicReady),
      detail: 'scheduled cases needing a clean auth packet',
      icon: ClipboardList,
      tone: 'primary',
    },
    {
      label: 'Payer Pending',
      value: String(metrics.payerPending),
      detail: 'submitted packets still in payer review',
      icon: TimerReset,
      tone: 'warning',
    },
    {
      label: 'OR Reconciliation',
      value: String(metrics.orReady),
      detail: 'approved cases ready for dictation or match-back',
      icon: CalendarClock,
      tone: 'success',
    },
    {
      label: 'RCM Exceptions',
      value: String(metrics.rcmExceptions),
      detail: 'items that can leak revenue or trigger denials',
      icon: AlertTriangle,
      tone: metrics.rcmExceptions > 0 ? 'danger' : 'success',
    },
  ];
}

export default function SurgeonConsole() {
  const { isDemoMode } = useDemoMode();
  const [workspace, setWorkspace] = useState<Workspace>('command-center');
  const workspaceScrollTargetRef = useRef<HTMLDivElement | null>(null);
  const baselineMetrics = isDemoMode ? DEMO_CONSOLE_METRICS : EMPTY_CONSOLE_METRICS;

  const { data: metrics = baselineMetrics } = useQuery({
    queryKey: ['surgeon-console-metrics', isDemoMode ? 'demo' : 'live'],
    queryFn: fetchConsoleMetrics,
    initialData: baselineMetrics,
    enabled: !isDemoMode,
    staleTime: 30_000,
    refetchInterval: isDemoMode ? false : 60_000,
  });

  const metricCards = useMemo(() => getMetricCards(metrics), [metrics]);
  const showWorkspace = useCallback((nextWorkspace: Workspace) => {
    setWorkspace(nextWorkspace);
    scheduleSurgeonWorkspaceScrollToTop(() => workspaceScrollTargetRef.current);
  }, []);

  return (
    <SurgeonLayout>
      <div className="w-full space-y-6">
        {isDemoMode && (
          <div className="flex items-center gap-2 rounded-lg border border-accent bg-accent/50 p-2.5 text-xs text-muted-foreground">
            <BadgeCheck className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium text-foreground">Demo Mode</span>
            <span>No external systems are touched.</span>
          </div>
        )}

        <ConsoleHero
          metrics={metrics}
          onStartAuth={() => showWorkspace('clinic-auth')}
          onStartPostOp={() => showWorkspace('todays-or')}
        />

        {workspace === 'command-center' && (
          <WorkspaceNavigation workspace={workspace} onChange={showWorkspace} />
        )}

        <div ref={workspaceScrollTargetRef} data-surgeon-workspace-scroll-target className="scroll-mt-20">
          {workspace === 'command-center' && (
            <CommandCenter
              metrics={metrics}
              metricCards={metricCards}
              onStartAuth={() => showWorkspace('clinic-auth')}
              onStartPostOp={() => showWorkspace('todays-or')}
            />
          )}

          {workspace === 'clinic-auth' && (
            <WorkspaceFrame
              title="Pre-Auth Build"
              subtitle="Create the defensible CPT universe before the case reaches the OR."
              rail={<WorkflowGuardrailRail mode="pre-auth" />}
            >
              <ClinicAuthWorkspace onDemoPreAuthSigned={() => showWorkspace('todays-or')} />
            </WorkspaceFrame>
          )}

          {workspace === 'todays-or' && (
            <WorkspaceFrame
              title="Post-Op Reconciliation"
              subtitle="Turn surgeon dictation into a matched, attested, export-ready record."
              rail={<WorkflowGuardrailRail mode="post-op" />}
            >
              <TodaysORWorkspace />
            </WorkspaceFrame>
          )}
        </div>
      </div>
    </SurgeonLayout>
  );
}

function ConsoleHero({
  metrics,
  onStartAuth,
  onStartPostOp,
}: {
  metrics: ConsoleMetrics;
  onStartAuth: () => void;
  onStartPostOp: () => void;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="p-5 sm:p-6 lg:p-8">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1.5 border-primary/30 text-primary">
            <ShieldCheck className="h-3.5 w-3.5" />
            Closed-loop revenue protection
          </Badge>
          {metrics.dataUnavailable && (
            <Badge variant="outline" className="gap-1.5 border-amber-300 text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              Live counts unavailable
            </Badge>
          )}
        </div>

        <div className="max-w-3xl space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Surgeon Command Center
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            One console for the surgeon's revenue-critical actions: authorize the right bundle, document what happened, reconcile the match, and block weak claims before they become denials.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button onClick={onStartAuth} className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Start Pre-Auth
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button onClick={onStartPostOp} variant="outline" className="gap-2">
            <Stethoscope className="h-4 w-4" />
            Open OR Reconciliation
          </Button>
        </div>
      </div>
    </section>
  );
}

function WorkspaceNavigation({
  workspace,
  onChange,
}: {
  workspace: Workspace;
  onChange: (workspace: Workspace) => void;
}) {
  return (
    <nav className="grid gap-2 rounded-lg border border-border bg-muted/30 p-1 sm:grid-cols-3">
      {WORKSPACES.map((item) => {
        const Icon = item.icon;
        const active = workspace === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onChange(item.id)}
            className={cn(
              'flex min-h-[64px] items-center gap-3 rounded-md px-4 py-3 text-left transition-all',
              active
                ? 'border border-border/70 bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
            )}
          >
            <span className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border',
              active ? 'border-primary/30 bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground',
            )}>
              <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{item.label}</span>
              <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function CommandCenter({
  metrics,
  metricCards,
  onStartAuth,
  onStartPostOp,
}: {
  metrics: ConsoleMetrics;
  metricCards: MetricCardConfig[];
  onStartAuth: () => void;
  onStartPostOp: () => void;
}) {
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-5">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {metricCards.map((metric) => (
            <MetricCard key={metric.label} metric={metric} />
          ))}
        </div>

        <Card className="border-border/70">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base">Closed-Loop Case Pipeline</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  The console is organized around the exact chain that protects the claim.
                </p>
              </div>
              <Badge variant="outline" className="w-fit gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                Hard gate model
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 lg:grid-cols-5">
              {CLOSED_LOOP_STAGES.map((stage, index) => (
                <PipelineStageCard key={stage.title} stage={stage} index={index} />
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-5 lg:grid-cols-2">
          <ActionCard
            icon={ClipboardList}
            title="Build the Authorization Universe"
            count={metrics.clinicReady}
            primary="Pre-op worklist"
            body="Capture the broad but defensible CPT set before surgery, then let the audit layer remove weak or unsupported lines before the payer packet is generated."
            actionLabel="Open Pre-Auth Builder"
            onAction={onStartAuth}
          />
          <ActionCard
            icon={Stethoscope}
            title="Reconcile the Actual Operation"
            count={metrics.orReady}
            primary="Post-op match-back"
            body="Start from the approved bundle, capture dictation, force the authorized/documented/confirmed match, and keep unsupported revenue out of export."
            actionLabel="Open Post-Op Queue"
            onAction={onStartPostOp}
          />
        </div>
      </div>

      <div className="space-y-5">
        <NextBestActionCard metrics={metrics} onStartAuth={onStartAuth} onStartPostOp={onStartPostOp} />
        <GuardrailMatrix />
      </div>
    </div>
  );
}

function WorkspaceFrame({
  title,
  subtitle,
  rail,
  children,
}: {
  title: string;
  subtitle: string;
  rail: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <section className="min-w-0 space-y-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        {children}
      </section>
      <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
        {rail}
      </aside>
    </div>
  );
}

function WorkflowGuardrailRail({
  mode,
}: {
  mode: 'pre-auth' | 'post-op';
}) {
  const items = mode === 'pre-auth'
    ? [
        ['Clinical proof', 'Images, conservative care, ROM, injections, biomarkers'],
        ['Code cart', 'Laterality, assistant, modifier, bundled-code review'],
        ['RCM audit', 'Payer-risk issues must resolve or route before auth'],
        ['Signed packet', 'Narrative, CPTs, ICD-10, attachments, audit trail'],
      ]
    : [
        ['Authorized bundle', 'The starting CPT universe is read-only'],
        ['Dictation capture', 'Surgeon says what actually happened'],
        ['Match-back', 'Authorized, documented, confirmed per CPT'],
        ['Export gate', 'No unsupported or unconfirmed line reaches billing'],
      ];

  return (
    <Card className="border-border/70">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-primary" />
          {mode === 'pre-auth' ? 'Pre-Auth Guardrails' : 'Post-Op Guardrails'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map(([label, detail]) => (
          <div key={label} className="rounded-md border border-border/70 bg-muted/20 p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">{label}</p>
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{detail}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function MetricCard({ metric }: { metric: MetricCardConfig }) {
  const Icon = metric.icon;
  const toneClass = {
    primary: 'border-primary/20 bg-primary/10 text-primary',
    success: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-600',
    warning: 'border-amber-500/20 bg-amber-500/10 text-amber-600',
    danger: 'border-destructive/20 bg-destructive/10 text-destructive',
  }[metric.tone];

  return (
    <Card className="border-border/70">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{metric.label}</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{metric.value}</p>
          </div>
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-md border', toneClass)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="mt-3 text-xs leading-5 text-muted-foreground">{metric.detail}</p>
      </CardContent>
    </Card>
  );
}

function PipelineStageCard({
  stage,
  index,
}: {
  stage: typeof CLOSED_LOOP_STAGES[number];
  index: number;
}) {
  const Icon = stage.icon;
  return (
    <div className="rounded-md border border-border/70 bg-muted/20 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex h-8 w-8 items-center justify-center rounded-md border border-primary/20 bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-xs font-mono text-muted-foreground">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <p className="text-sm font-semibold text-foreground">{stage.title}</p>
      <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{stage.owner}</p>
      <Separator className="my-3" />
      <p className="text-xs leading-5 text-foreground">{stage.gate}</p>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{stage.risk}</p>
    </div>
  );
}

function ActionCard({
  icon: Icon,
  title,
  count,
  primary,
  body,
  actionLabel,
  onAction,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
  primary: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Card className="border-border/70">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <p className="font-semibold text-foreground">{title}</p>
              <Badge variant="secondary">{count}</Badge>
            </div>
            <p className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">{primary}</p>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{body}</p>
            <Button onClick={onAction} variant="outline" className="mt-4 gap-2">
              {actionLabel}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NextBestActionCard({
  metrics,
  onStartAuth,
  onStartPostOp,
}: {
  metrics: ConsoleMetrics;
  onStartAuth: () => void;
  onStartPostOp: () => void;
}) {
  const action = metrics.rcmExceptions > 0
    ? {
        title: 'Clear RCM exceptions first',
        body: 'Denial exposure is already present. Work the exception queue before creating more downstream variance.',
        button: 'Open Pre-Auth Builder',
        onClick: onStartAuth,
        icon: AlertTriangle,
      }
    : metrics.orReady > 0
      ? {
          title: 'Reconcile approved OR cases',
          body: 'Approved cases are closest to revenue. Dictation and match-back should happen before memory fades.',
          button: 'Open Post-Op Queue',
          onClick: onStartPostOp,
          icon: Stethoscope,
        }
      : {
          title: 'Build the next clean auth packet',
          body: 'Start with the clinic queue and create a broad, defensible authorization bundle.',
          button: 'Open Pre-Auth Builder',
          onClick: onStartAuth,
          icon: ClipboardList,
        };
  const Icon = action.icon;

  return (
    <Card className="border-primary/25 bg-primary/[0.03]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" />
          Next Best Action
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{action.title}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{action.body}</p>
          </div>
        </div>
        <Button onClick={action.onClick} className="mt-5 w-full gap-2">
          {action.button}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

function GuardrailMatrix() {
  return (
    <Card className="border-border/70">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Denial Defense Doctrine</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {GUARDRAILS.map((guardrail) => (
          <div key={guardrail} className="flex items-start gap-3 rounded-md border border-border/70 bg-muted/20 p-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-sm leading-6 text-muted-foreground">{guardrail}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
